import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import ProductCard from '../components/products/ProductCard';
import { products, categories, siteImages } from '../data/mock';
import { ChevronDown, LayoutGrid } from 'lucide-react';

const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortBy, setSortBy] = useState('featured');
  const selectedCategory = searchParams.get('category') || '';

  useEffect(() => { document.title = 'Shop Bike Parts & Accessories | Trailside Cycling'; }, []);

  const filteredProducts = useMemo(() => {
    let result = [...products];
    if (selectedCategory) result = result.filter(p => p.categorySlug === selectedCategory);
    switch (sortBy) {
      case 'price-low': result.sort((a, b) => a.price - b.price); break;
      case 'price-high': result.sort((a, b) => b.price - a.price); break;
      case 'name': result.sort((a, b) => a.name.localeCompare(b.name)); break;
      default: break;
    }
    return result;
  }, [selectedCategory, sortBy]);

  const handleCategoryChange = (slug) => { if (slug) setSearchParams({ category: slug }); else setSearchParams({}); };
  const currentCategory = categories.find(c => c.slug === selectedCategory);

  return (
    <div className="min-h-screen bg-white text-dark-700">
      <Header />
      <CartDrawer />
      <main>
        {/* Banner */}
        <section className="relative bg-dark-950 overflow-hidden">
          <img src={siteImages.shopBanner} alt="" className="absolute inset-0 w-full h-full object-cover opacity-35" />
          <div className="absolute inset-0 bg-gradient-to-r from-dark-950 via-dark-950/80 to-athletic-900/50" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <nav className="text-sm text-dark-300 mb-3"><Link to="/" className="hover:text-white">Home</Link><span className="mx-2">/</span><span className="text-white">{currentCategory ? currentCategory.name : 'Shop All'}</span></nav>
            <h1 className="font-display text-4xl sm:text-6xl font-bold text-white">{currentCategory ? currentCategory.name : 'Shop All Gear'}</h1>
            <p className="mt-3 text-dark-200 max-w-2xl">{currentCategory ? currentCategory.description : 'A rider-tested collection of bike parts, accessories and supplies \u2014 safety, tools, ride tech, carriers and complete bikes.'}</p>
          </div>
        </section>

        {/* Sticky category pill bar */}
        <section className="sticky top-[72px] z-30 bg-white/95 backdrop-blur border-b border-dark-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              <button onClick={() => handleCategoryChange('')} className={`px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${!selectedCategory ? 'bg-athletic-600 text-white' : 'bg-dark-50 text-dark-700 hover:bg-dark-100'}`}>All ({products.length})</button>
              {categories.map((cat) => (<button key={cat.id} onClick={() => handleCategoryChange(cat.slug)} className={`px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${selectedCategory === cat.slug ? 'bg-athletic-600 text-white' : 'bg-dark-50 text-dark-700 hover:bg-dark-100'}`}>{cat.name} ({cat.productCount})</button>))}
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex items-center justify-between gap-3 mb-6">
            <p className="flex items-center gap-2 text-sm text-dark-600"><LayoutGrid className="w-4 h-4 text-athletic-600" /><span className="font-semibold text-dark-900">{filteredProducts.length}</span> product{filteredProducts.length !== 1 ? 's' : ''}</p>
            <div className="relative">
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="appearance-none bg-white border border-dark-200 text-dark-700 text-sm rounded-full px-5 py-2.5 pr-9 focus:outline-none focus:ring-2 focus:ring-athletic-500 cursor-pointer">
                <option value="featured">Featured</option><option value="price-low">Price: Low to High</option><option value="price-high">Price: High to Low</option><option value="name">Name: A-Z</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400 pointer-events-none" />
            </div>
          </div>
          {filteredProducts.length === 0 ? (
            <div className="bg-dark-50 rounded-2xl p-12 text-center border border-dark-100"><p className="text-dark-600">No products found in this category.</p></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">{filteredProducts.map((p) => (<ProductCard key={p.id} product={p} />))}</div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ShopPage;
